/** Access tiers, mirroring the server's AccountRole enum. */
export type AccountRole = 'owner' | 'contributor' | 'coach' | 'viewer' | 'sponsor' | 'athlete';

/** Every role, in the order shown on the role picker and the subscription screen. */
export const ALL_ROLES: AccountRole[] = ['owner', 'contributor', 'coach', 'viewer', 'sponsor', 'athlete'];

export const ROLE_LABELS: Record<AccountRole, string> = {
  owner: 'Organizer/Head Coach',
  contributor: 'Contributor',
  coach: 'Coach',
  viewer: 'Viewer',
  sponsor: 'Sponsor',
  athlete: 'Athlete',
};

export const ROLE_DESCRIPTIONS: Record<AccountRole, string> = {
  owner: 'Full access to start a program, add teams, invite coaches, contributors, athletes, sponsors, etc.',
  contributor: 'Ability to record games for a team.',
  coach: 'Ability to manage one team and invite athletes.',
  viewer: 'Can review games, but not record or change them.',
  sponsor: "Link to a program or team and see highlight data describing the team's season.",
  athlete: 'Link to a program or team and see the data generated from recorded games.',
};

/** Whether picking a role (or, once priced, paying for it) has actually taken effect. */
export type MembershipStatus = 'pending' | 'active';

/** One account the signed-in user can reach, and what they may do there. */
export interface AccountAccess {
  accountId: string;
  accountName: string;
  role: AccountRole;
  canWrite: boolean;
  canAdminister: boolean;
}

export interface CurrentUser {
  id: string;
  email: string;
  displayName: string;
  /**
   * The kind of user this person is platform-wide — picked and paid for once at
   * registration, independent of `accounts`. Null until they choose.
   */
  role: AccountRole | null;
  /**
   * True until `role` is chosen and active — a person can hold a role and simply not
   * be in any program yet, so this is never derived from `accounts.length`.
   */
  needsRoleSelection: boolean;
  /**
   * Epoch ms `role` lapses — null until first activated. A yearly subscription: once
   * this passes, `needsRoleSelection` goes true again unless it's currently free, in
   * which case the server renews it silently instead.
   */
  roleExpiresAt: number | null;
  accounts: AccountAccess[];
  /** Where a new game lands by default; null when the user cannot write anywhere. */
  defaultWriteAccountId: string | null;
  /** Platform-wide administration access, unrelated to any account's roles. */
  systemAdmin: boolean;
}

/** A registered person, as shown on the system-admin user list. */
export interface AdminUser {
  id: string;
  email: string;
  displayName: string;
  createdAt: number;
  enabled: boolean;
  systemAdmin: boolean;
}

/**
 * An operational account, as shown on the system-admin account list — for finding
 * which account a support request is about, never for reaching its game data.
 */
export interface AdminAccountSummary {
  id: string;
  name: string;
  createdAt: number;
  ownerEmail: string | null;
  ownerDisplayName: string | null;
  memberCount: number;
}

/** The signed-in person's own security questions — text only, never the answers. */
export interface SecurityQuestionsStatus {
  configured: boolean;
  question1: string | null;
  question2: string | null;
  question3: string | null;
}

export interface SetSecurityQuestions {
  currentPassword: string;
  question1: string;
  answer1: string;
  question2: string;
  answer2: string;
  question3: string;
  answer3: string;
}

/** A person with access to an account, as shown on the members screen. */
export interface AccountMember {
  userId: string;
  email: string;
  displayName: string;
  role: AccountRole;
  grantedAt: number;
}

export interface Credentials {
  email: string;
  password: string;
}

/**
 * currentPassword is always required, even to change only the display name — see
 * the server-side UpdateProfileRequest doc. newPassword is optional; omit it to
 * leave the password unchanged.
 */
export interface ProfileUpdate {
  displayName: string;
  email: string;
  currentPassword: string;
  newPassword?: string;
}

/** Registering creates only the login — no account, no write access anywhere yet. */
export interface Registration extends Credentials {
  displayName: string;
}

/** A passkey registered for sign-in, as shown on the passkey management screen. */
export interface Passkey {
  id: string;
  label: string;
  createdAt: number;
  lastUsedAt: number | null;
}
